import Highcharts, { YAxisOptions } from "highcharts";
import { TimeframeTypeEnum } from "../enums/TimeframeTypeEnum";
import { Dictionary, PlotLineOrBand, XAxisOptions, XAxisPlotBandsLabelOptions, XAxisPlotBandsOptions, XAxisPlotLinesLabelOptions, XAxisPlotLinesOptions, YAxisPlotLinesOptions } from "highcharts";
import { ISetPointViewModel } from "../../services/alarm-setpoints-data-service/Models";

export type TooltipOptions =
    {
        ignoreScrollTop: boolean | undefined;
        setTipColor: Function | undefined;
        setTipMaxWidth: Function;
        setTipOpen: Function;
        setTipPosition: Function;
        setTipText: Function;
        tipXMargin: number;
    }

export function getHighchartsTooltipFormat(timeframe: TimeframeTypeEnum) {

    let labelFormat: string;

    switch (timeframe) {
        case TimeframeTypeEnum.Daily:
        case TimeframeTypeEnum.Monthly:
        case TimeframeTypeEnum.Yearly:
            labelFormat = "%m/%d/%Y";
            break;

        default:
            labelFormat = "%m/%d/%Y %l:%M:%S %p";
    }

    return labelFormat;
}

export interface ISetpointPlotlinesOptions extends YAxisPlotLinesOptions {
    tooltipText: string
}

export interface IxAxisPlotBandsLabelOptions extends XAxisPlotBandsLabelOptions {
    tooltipText: string
}

export interface IxAxisPlotLinesLabelOptions extends XAxisPlotLinesLabelOptions {
    tooltipText: string
}

export function isXAxisPlotBandsOptions(options: XAxisPlotBandsOptions | XAxisPlotLinesOptions): options is XAxisPlotBandsOptions {
    return (options as XAxisPlotBandsOptions).borderColor !== undefined;
}

export function isXAxisPlotLinesOptions(options: XAxisPlotBandsOptions | XAxisPlotLinesOptions): options is XAxisPlotLinesOptions {
    return (options as XAxisPlotLinesOptions).dashStyle !== undefined;
}

export function getSetpointPlotlines(setpoints: ISetPointViewModel[]): YAxisPlotLinesOptions[] {

    let setpointPlotLines: YAxisPlotLinesOptions[] = [];

    if (setpoints && setpoints.length > 0) {

        setpointPlotLines = setpoints.map(sp => {
            let item: ISetpointPlotlinesOptions = {
                tooltipText: sp.setPointName,
                color: sp.alarmColor,
                dashStyle: "LongDash",

                events: {
                },
                label: {
                    //text: sp.setPointName,
                    align: "right",
                    x: -10,
                    //y: 16, // Changed because the chart usually has room at the top but not the bottom
                    style: {
                        fontFamily: "xSegoe UI, Selawik Semilight",
                        color: sp.alarmColor
                    }
                },
                value: sp.value,
                //tooltipText: sp.setPointName,
                width: 2, // I don't know why 1 does not work now
                zIndex: 9999
            };

            return item;
        });
    }

    return setpointPlotLines;
}

export function setupTooltips(axis: Array<XAxisOptions> | Array<YAxisOptions> | undefined, tooltipOptions: TooltipOptions): void {

    if (axis !== undefined) {

        const highChartsVersion = +(Highcharts as any).version.replace(/\./g, '');

        // Display time from Highcharts
        const tooltipDisplayTime: number = 500;
        let displayTimeTimerID: number | undefined;

        const clearTipDisplayTimer = () => {

            // Reset the timer for hiding the Tooltip
            if (displayTimeTimerID) {
                clearTimeout(displayTimeTimerID);
                displayTimeTimerID = undefined;
            }
        }

        const hideTooltip = () => {

            displayTimeTimerID = window.setTimeout(() => {

                tooltipOptions.setTipOpen(false);
                tooltipOptions.setTipPosition(undefined);
                tooltipOptions.setTipText(undefined);
            }, tooltipDisplayTime);
        }

        const positionTooltip = (tooltipOptions: TooltipOptions, plotLineOrBand: any,
            options: XAxisPlotBandsOptions | XAxisPlotLinesOptions,
            eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) => {

            clearTipDisplayTimer();

            const mouseEvent = eventArguments as MouseEvent;
            const targetElement = eventArguments?.target as HTMLElement;
            const chartContainer = targetElement?.closest(".highcharts-container") as HTMLElement;

            if (chartContainer) {

                //console.log(`client: ${mouseEvent.clientX}, ${mouseEvent.clientY}  offset: ${mouseEvent.offsetX}, ${mouseEvent.offsetY}`);

                // use the parent and get the position relative to the viewport minus the offset relative to the parent.
                // This is used to position the tooltip just to the right of the icon, based on where the mouse is relative
                // to the document
                const parentRect = chartContainer.getBoundingClientRect();

                // parentRect.width
                const chartWidth: number = plotLineOrBand.axis?.chart?.chartWidth ?? parentRect.width;

                // Get the first scrolling Div if it exists.
                //const scrollParent: HTMLElement = this._getScrollParent(chartContainer);
                //const scrollTop: number = scrollParent ? scrollParent.scrollTop : 0;

                const offsetX = parentRect.left;

                //console.log("MY OFFSET: " + offsetX);

                let scrollTop = 0;

                // I am not sure what changed between now and when Kurt helped me fix the tooltip position
                //  but now this code isn't needed and I don't know why it was needed and why it isn't now.
                //  Maybe it was the change Kurt had to make in Graph.tsx for Tanks with the Flex settings? 
                //  That would make sense.
                //  For now use a flag to control it. 
                if (!tooltipOptions.ignoreScrollTop) {
                    let parent = chartContainer.parentElement;
                    while (parent) {
                        scrollTop += parent.scrollTop;
                        parent = parent.parentElement;
                    }
                }

                const elementTopOffset = (chartContainer?.offsetTop ?? mouseEvent.offsetY) - scrollTop;

                //User Story 53044: Research - 2025 Q2 - Review GAS report
                let x; // = mouseEvent.offsetX;
                let maxWidth; // = undefined;

                if (mouseEvent.pageX - offsetX < (chartWidth - tooltipOptions.tipXMargin)) {
                    x = mouseEvent.pageX - offsetX;
                    maxWidth = undefined;
                } else {
                    x = chartWidth - tooltipOptions.tipXMargin;
                    maxWidth = tooltipOptions.tipXMargin;
                }

                tooltipOptions.setTipMaxWidth(maxWidth);

                tooltipOptions.setTipPosition({
                    x: x,
                    // Position the tooltip at the top to keep it out of the way of the
                    //  graph tooltip
                    y: elementTopOffset
                });
            }
        }

        const tooltipMouseOverCallback = (tooltipOptions: TooltipOptions, plotLineOrBand: any,
            options: XAxisPlotBandsOptions | XAxisPlotLinesOptions,
            eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) => {

            const title: IxAxisPlotBandsLabelOptions | IxAxisPlotLinesLabelOptions | undefined =
                isXAxisPlotBandsOptions(options)
                    ?
                    options.label as IxAxisPlotBandsLabelOptions
                    :
                    isXAxisPlotLinesOptions(options)
                        ?
                        options.label as IxAxisPlotLinesLabelOptions
                        :
                        undefined;

            positionTooltip(tooltipOptions, plotLineOrBand, options, eventArguments);

            if (tooltipOptions.setTipColor !== undefined) {
                tooltipOptions.setTipColor(options.color);
            }

            tooltipOptions.setTipText(title?.tooltipText);
            tooltipOptions.setTipOpen(true);
        };

        const tooltipMouseOutCallback = () => {
            hideTooltip();
        };

        const tooltipMouseMoveCallback = (tooltipOptions: TooltipOptions, plotLineOrBand: any,
            options: XAxisPlotBandsOptions | XAxisPlotLinesOptions,
            eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) => {

            positionTooltip(tooltipOptions, plotLineOrBand, options, eventArguments);
        }

        // On plotlines add the mouseover and mouseout methods
        axis.forEach(xa => {
            xa.plotLines?.forEach(pl => {

                // Convert fontawesome to png
                // http://fa2png.io/ 
                // Must be set on the backend so Quick Trending works.
                //pl.imagePath = pl.plotLineType.toLowerCase() === "alarm" ? "./images/bell-red.png" : "./images/sticky-note-gray.png";

                pl.events =
                {
                    mouseover: function (eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) {

                        let plotLineInstance: PlotLineOrBand | undefined = undefined;

                        // SVG Image elements are different after Highcharts version 9.0.1 so the plotline-image plugin must
                        //  add a property to store the plotline instance so we can get to the plotline instance in this code.
                        //  In older versions this was the plotline instance
                        if (highChartsVersion > 901 && (this as any).plotline) {

                            plotLineInstance = (this as any).plotline;
                        } else {

                            plotLineInstance = this;
                        }

                        tooltipMouseOverCallback(tooltipOptions, plotLineInstance, pl, eventArguments);
                    },
                    mouseout: function (eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) {
                        tooltipMouseOutCallback();
                    },
                    mousemove: function (eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) {

                        let plotLineInstance: PlotLineOrBand | undefined = undefined;

                        // SVG Image elements are different after Highcharts version 9.0.1 so the plotline-image plugin must
                        //  add a property to store the plotline instance so we can get to the plotline instance in this code.
                        //  In older versions this was the plotline instance
                        if (highChartsVersion > 901 && (this as any).plotline) {

                            plotLineInstance = (this as any).plotline;
                        } else {

                            plotLineInstance = this;
                        }

                        tooltipMouseMoveCallback(tooltipOptions, plotLineInstance, pl, eventArguments);
                    }
                };
            });
        });

        // On plotbands add the mouseover and mouseout methods
        axis.forEach(xa => {
            xa.plotBands?.forEach(pb => {

                pb.events =
                {
                    mouseover: function (eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) {
                        tooltipMouseOverCallback(tooltipOptions, this, pb, eventArguments);
                    },
                    mouseout: function (eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) {
                        tooltipMouseOutCallback();
                    },
                    mousemove: function (eventArguments?: Event | Dictionary<PlotLineOrBand> | undefined) {
                        tooltipMouseMoveCallback(tooltipOptions, this, pb, eventArguments);
                    }
                };
            });
        });
    }
}